import { AvatarUtils } from "../../../common/avatar_utils.js";
import { getFlagByCountryCode } from "../../../common/flag_utils.js";
import { ImgID } from "../../../image_loader/img_id.js";
import { ImagesHolder } from "../../../image_loader/images_holder.js";
import { TextStyleScheme } from "../../common_ui_elements/text_style_scheme.js";
import { getMeasureContext } from "../../dom_backend/null_canvas_context.js";
import type { LeaderboardScreenPlayerInfoConfig } from "./leaderboard_screen_player_info_config.js";

export interface LeaderboardRowsGeometry
{
  width: number;
  height: number;
  step: number;
  x: number;
  top_pad: number;
}

export interface LeaderboardRowRange { first: number; last: number; }

interface RenderedRow
{
  node: HTMLDivElement;
  svg: SVGSVGElement;
  texts: SVGTextElement[];
  score_stroke: SVGTextElement;
  avatar: HTMLImageElement;
  flag: HTMLImageElement;
  index: number;
  player: LeaderboardScreenPlayerInfoConfig;
}

const SVG_NS = "http://www.w3.org/2000/svg";
const ROW_CSS = `
.ui-leaderboard-native-rows{position:absolute;left:0;top:0;width:100%;pointer-events:none;contain:layout style}
.ui-leaderboard-native-row{position:absolute;left:var(--lb-x);width:var(--lb-w);height:var(--lb-step);contain:layout style;background-image:var(--lb-badge,none),var(--lb-template,none);background-position:var(--lb-badge-x) var(--lb-badge-y),0 0;background-size:var(--lb-badge-w) var(--lb-badge-h),var(--lb-w) var(--lb-card-h);background-repeat:no-repeat}
.ui-leaderboard-native-row>svg{position:absolute;left:0;top:0;width:100%;height:100%;overflow:visible;font-family:var(--lb-font);font-weight:900}
.ui-leaderboard-native-row text{paint-order:stroke fill;stroke-linejoin:round;stroke-linecap:round;stroke-miterlimit:2}
.ui-leaderboard-native-avatar{position:absolute;left:var(--lb-avatar-x);top:var(--lb-avatar-y);width:var(--lb-avatar-w);height:var(--lb-avatar-h);box-sizing:border-box;padding:var(--lb-avatar-pad);border-radius:50%;background:linear-gradient(to bottom,#F9F4C9,#B5A084);object-fit:cover;overflow:hidden}
.ui-leaderboard-native-flag{position:absolute;left:var(--lb-flag-x);top:var(--lb-flag-y);width:var(--lb-flag-w);height:var(--lb-flag-h);border-radius:var(--lb-flag-radius);object-fit:fill}
`;

function imageCss( url: string | null ): string
{
  return url ? `url(${JSON.stringify( url )})` : "none";
}

function attribute( node: Element, name: string, value: string | null ): void
{
  if ( node.getAttribute( name ) === value ) return;
  if ( value === null ) node.removeAttribute( name );
  else node.setAttribute( name, value );
}

/**
 * Eight DOM elements per row and one shared backdrop keep row rebinding small. The regular
 * scroll path reuses a viewport-sized pool; omitting the range builds the complete board for
 * native-scroll experiments. Neither path creates row canvases or UIElement trees.
 */
export class LeaderboardRowsView
{
  public readonly node: HTMLDivElement;
  private readonly style: HTMLStyleElement;
  private readonly rows = new Map<number, RenderedRow>();
  private readonly free_rows: RenderedRow[] = [];
  private geometry_key = "";
  private template_url: string | null = null;

  public get builtRowCount(): number { return this.rows.size + this.free_rows.length; }
  public get boundRowCount(): number { return this.rows.size; }

  constructor( host: HTMLElement )
  {
    this.node = document.createElement( "div" );
    this.node.className = "ui-leaderboard-native-rows";
    this.style = document.createElement( "style" );
    this.style.textContent = ROW_CSS;
    this.node.appendChild( this.style );
    host.appendChild( this.node );
  }

  public setTemplateUrl( url: string ): void
  {
    if ( this.template_url === url ) return;
    this.template_url = url;
    const css = this.node.style;
    css.setProperty( "--lb-template", imageCss( url ) );
    css.visibility = url ? "" : "hidden";
  }

  public render( players: readonly LeaderboardScreenPlayerInfoConfig[], geometry: LeaderboardRowsGeometry, template_url: string, range?: LeaderboardRowRange ): void
  {
    const { width, height, step, x, top_pad } = geometry;
    const wd = ( value: number ) => LayoutScaler.widthFromDesign( value );
    const hd = ( value: number ) => LayoutScaler.heightFromDesign( value );
    const xd = ( value: number ) => LayoutScaler.xFromDesign( value );
    const yd = ( value: number ) => LayoutScaler.yFromDesign( value );
    const avatar_w = wd( 118 );
    const avatar_h = hd( 118 );
    const border = wd( 4 );
    const flag_w = wd( 55 );
    const flag_h = hd( 55 );
    const stats_w = wd( 260 );
    const stats_h = hd( 110 );
    const stats_x = width - xd( 42 ) - wd( 25 ) - wd( 65 ) - wd( 16 ) - stats_w;
    const stats_y = ( height - stats_h ) / 2 - yd( 3 );
    const name_x = xd( 322 );
    const name_w = Math.max( 1, stats_x - wd( 16 ) - name_x );
    const base_size = LayoutScaler.textSizeFromDesign( 45 );
    const font = TextStyleScheme.geologica;
    const css = this.node.style;
    const values: Record<string, number> = {
      x, w: width, step, "card-h": height,
      "badge-x": xd( 32 ), "badge-y": yd( 9 ) + ( hd( 130 ) - hd( 129 ) ) / 2,
      "badge-w": wd( 130 ), "badge-h": hd( 129 ),
      "avatar-x": xd( 186 ) - border / 2, "avatar-y": yd( 16 ) - border / 2,
      "avatar-w": avatar_w + border, "avatar-h": avatar_h + border, "avatar-pad": border * 1.5,
      "flag-x": xd( 166.37 ) + flag_w * 0.05, "flag-y": yd( 83.5 ) + flag_h * 0.05,
      "flag-w": flag_w * 0.9, "flag-h": flag_h * 0.9, "flag-radius": Math.min( flag_w, flag_h ) * 0.1,
    };
    const geometry_key = [ top_pad, ...Object.values( values ) ].join( "," );
    const geometry_changed = geometry_key !== this.geometry_key;
    if ( geometry_changed )
    {
      this.geometry_key = geometry_key;
      for ( const [ name, value ] of Object.entries( values ) )
        css.setProperty( `--lb-${name}`, `${value}px` );
      css.setProperty( "--lb-font", font );
    }
    const layer_height = range ? "0px" : `${Math.max( 0, top_pad + players.length * step )}px`;
    if ( css.height !== layer_height ) css.height = layer_height;
    this.setTemplateUrl( template_url );

    const first = Math.max( 0, range?.first ?? 0 );
    const last = Math.min( players.length - 1, range?.last ?? players.length - 1 );
    for ( const [ index, row ] of this.rows )
    {
      if ( index < first || index > last || players[index]?.is_player )
      {
        this.rows.delete( index );
        delete row.node.dataset.leaderboardIndex;
        this.free_rows.push( row );
      }
    }

    const fragment = document.createDocumentFragment();
    const measure = getMeasureContext();
    const local_avatar_fallback = ImagesHolder.getUrl( AvatarUtils.getAvatarImgId( 0 ) );
    const default_flag = ImagesHolder.getUrl( ImgID.Flag_unknown );
    const badge_ids = [ ImgID.UI_LeaderboardStarOrange, ImgID.UI_LeaderboardStarBlue, ImgID.UI_LeaderboardStarBrown ];
    for ( let index = first; index <= last; index++ )
    {
      const player = players[index];
      // The existing sticky card supplies this row and its native compositor movement.
      if ( player.is_player ) continue;
      const current = this.rows.get( index );
      if ( current && current.player === player && !geometry_changed ) continue;
      const reused = current ?? this.free_rows.pop();
      const row = reused?.node ?? document.createElement( "div" );
      if ( !reused ) row.className = "ui-leaderboard-native-row";
      row.dataset.leaderboardIndex = String( index );
      const row_top = `${top_pad + index * step}px`;
      if ( row.style.top !== row_top ) row.style.top = row_top;
      if ( row.style.visibility === "hidden" ) row.style.visibility = "";
      if ( player.rating >= 1 && player.rating <= 3 )
        row.style.setProperty( "--lb-badge", imageCss( ImagesHolder.getUrl( badge_ids[player.rating - 1] ) ) );
      else if ( reused && reused.player.rating >= 1 && reused.player.rating <= 3 )
        row.style.removeProperty( "--lb-badge" );

      const svg = reused?.svg ?? document.createElementNS( SVG_NS, "svg" );
      attribute( svg, "width", String( width ) );
      attribute( svg, "height", String( step ) );
      if ( !reused ) svg.setAttribute( "aria-hidden", "true" );
      let text_index = 0;
      const row_texts: SVGTextElement[] = [];
      const text = ( value: string, left: number, center_y: number, box_w: number, box_h: number, color: string,
        outline = 0, outline_color = "", fit = false, centered = true ): SVGTextElement =>
      {
        const el = reused?.texts[text_index] ?? document.createElementNS( SVG_NS, "text" );
        text_index++;
        row_texts.push( el );
        if ( reused && !geometry_changed && el.textContent === value ) return el;
        let size = base_size;
        measure.font = `normal 900 ${size}px ${font}`;
        measure.textBaseline = "alphabetic";
        let metrics = measure.measureText( value );
        if ( fit )
        {
          size = Math.max( LayoutScaler.textSizeFromDesign( 28 ), size * Math.min( 1,
            metrics.width > 0 ? box_w / metrics.width : 1, box_h / ( size * 1.1 ) ) );
          if ( size !== base_size )
          {
            measure.font = `normal 900 ${size}px ${font}`;
            metrics = measure.measureText( value );
          }
        }
        if ( el.textContent !== value ) el.textContent = value;
        attribute( el, "font-size", String( size ) );
        attribute( el, "fill", color );
        attribute( el, "x", String( left + ( centered ? box_w / 2 : 0 ) ) );
        attribute( el, "y", String( center_y + ( metrics.actualBoundingBoxAscent - metrics.actualBoundingBoxDescent ) / 2 ) );
        if ( centered ) attribute( el, "text-anchor", "middle" );
        attribute( el, "textLength", metrics.width > box_w ? String( box_w ) : null );
        attribute( el, "lengthAdjust", metrics.width > box_w ? "spacingAndGlyphs" : null );
        if ( outline > 0 )
        {
          attribute( el, "stroke", outline_color );
          attribute( el, "stroke-width", String( outline ) );
        }
        if ( !reused ) svg.appendChild( el );
        return el;
      };
      text( String( player.rating ), xd( 32 ), yd( 9 ) + yd( 3 ) + hd( 130 ) / 2,
        wd( 130 ), hd( 130 ), "#FFFFFF", LayoutScaler.textBorderSizeFromDesign( 8 ), "#05083B" );
      text( player.name, name_x, yd( 12 + 130 / 2 ), name_w, hd( 60 ), "#07195B", 0, "", true, false );
      const score = text( String( player.score ).replace( /\B(?=(\d{3})+(?!\d))/g, " " ), stats_x, stats_y + yd( 48 ) + hd( 58 ) / 2,
        stats_w, hd( 58 ), "#FFFFFF" );
      // The original score paints its outline two design pixels below the fill. Keep that
      // offset explicitly; ordinary paint-order cannot place the stroke and fill separately.
      const score_stroke = reused?.score_stroke ?? score.cloneNode( true ) as SVGTextElement;
      if ( reused )
      {
        if ( score_stroke.textContent !== score.textContent ) score_stroke.textContent = score.textContent;
        for ( const name of [ "font-size", "x", "text-anchor", "textLength", "lengthAdjust" ] )
          attribute( score_stroke, name, score.getAttribute( name ) );
      }
      attribute( score_stroke, "fill", "none" );
      attribute( score_stroke, "stroke", "#65091C" );
      attribute( score_stroke, "stroke-width", String( LayoutScaler.textBorderSizeFromDesign( 11 ) ) );
      attribute( score_stroke, "y", String( Number( score.getAttribute( "y" ) ) + yd( 2 ) ) );
      if ( !reused )
      {
        svg.insertBefore( score_stroke, score );
        row.appendChild( svg );
      }

      const avatar = reused?.avatar ?? document.createElement( "img" );
      if ( !reused )
      {
        avatar.className = "ui-leaderboard-native-avatar";
        avatar.alt = "";
        avatar.loading = "lazy";
        avatar.decoding = "async";
        avatar.draggable = false;
      }
      const fallback = ImagesHolder.getUrl( AvatarUtils.getAvatarImgId( player.avatar_id ) ) ?? local_avatar_fallback;
      const facebook = player.avatar_id === AvatarUtils.FACEBOOK_AVATAR_ID && player.facebook_av_url;
      const avatar_url = facebook || fallback;
      avatar.onerror = null;
      if ( avatar_url && avatar.getAttribute( "src" ) !== avatar_url ) avatar.src = avatar_url;
      if ( facebook && fallback )
        avatar.onerror = () => { avatar.onerror = null; avatar.src = fallback; };
      if ( !reused ) row.appendChild( avatar );

      const flag = reused?.flag ?? document.createElement( "img" );
      if ( !reused )
      {
        flag.className = "ui-leaderboard-native-flag";
        flag.alt = "";
        flag.loading = "lazy";
        flag.decoding = "async";
        flag.draggable = false;
      }
      const flag_url = ImagesHolder.getUrl( getFlagByCountryCode( player.country_code ) ) ?? default_flag;
      if ( flag_url && flag.getAttribute( "src" ) !== flag_url ) flag.src = flag_url;
      if ( !reused )
      {
        row.appendChild( flag );
        fragment.appendChild( row );
      }
      this.rows.set( index, { node: row, svg, texts: row_texts, score_stroke, avatar, flag, index, player } );
    }
    this.node.appendChild( fragment );
    // A full-board experiment followed by a bounded range must not retain its large pool.
    const spare = Math.max( 0, last - first + 1 - this.rows.size );
    while ( this.free_rows.length > spare ) this.free_rows.pop()!.node.remove();
    // Reassigned rows never need to hide between two synchronous bindings. Only the unused
    // remainder is hidden, avoiding inherited visibility invalidation on the usual scroll.
    for ( const row of this.free_rows )
      if ( row.node.style.visibility !== "hidden" ) row.node.style.visibility = "hidden";
  }

  public clear(): void
  {
    this.node.replaceChildren( this.style );
    this.node.style.height = "0px";
    this.rows.clear();
    this.free_rows.length = 0;
  }

  public destroy(): void
  {
    this.clear();
    this.node.remove();
  }
}
